import { InputHTMLAttributes, ReactNode } from 'react';
import Button from './Button';
import Icon from './Icon';
import Modal from './Modal';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';

export function Input({
  label,
  required,
  helper,
  error,
  id,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  helper?: string;
  error?: string;
}) {
  const { colors } = useTheme();
  const inputId = id || `field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const messageId = `${inputId}-message`;
  return (
    <label htmlFor={inputId} style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 8 }}>
        {label}{required ? ' *' : ''}
      </span>
      <input
        {...props}
        id={inputId}
        required={required}
        aria-invalid={Boolean(error)}
        aria-describedby={helper || error ? messageId : undefined}
        style={{
          width: '100%', minHeight: 56, padding: '0 16px', borderRadius: 12,
          border: `1px solid ${error ? colors.text : colors.line}`,
          backgroundColor: colors.input, color: colors.inputText, fontSize: 17,
        }}
      />
      {(error || helper) && (
        <span id={messageId} style={{ display: 'block', marginTop: 6, fontSize: 14, lineHeight: 1.4, color: error ? colors.text : colors.textMuted }}>
          {error || helper}
        </span>
      )}
    </label>
  );
}

export function SectionHeader({ title }: { title: string }) {
  const { colors } = useTheme();
  return <h2 style={{ margin: '28px 0 10px', fontSize: 20, lineHeight: 1.3, fontWeight: 700, color: colors.text }}>{title}</h2>;
}

export function ListRow({
  icon,
  label,
  value,
  chevron = true,
  onPress,
}: {
  icon?: string;
  label: string;
  value?: ReactNode;
  chevron?: boolean;
  onPress?: () => void;
}) {
  const { colors } = useTheme();
  const content = (
    <>
      {icon ? (
        <span style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: colors.line, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <Icon name={icon} size={17} color={colors.text} />
        </span>
      ) : null}
      <span style={{ flex: 1, minWidth: 0, fontSize: 17, fontWeight: 600, color: colors.text, textAlign: 'left' }}>{label}</span>
      {value ? <span style={{ fontSize: 15, color: colors.textSub, textAlign: 'right' }}>{value}</span> : null}
      {chevron && onPress ? <Icon name="chevron-right" size={14} color={colors.textMuted} /> : null}
    </>
  );
  const style = {
    width: '100%', minHeight: 64, display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 0', border: 'none', borderBottom: `1px solid ${colors.line}`,
    background: 'transparent', cursor: onPress ? 'pointer' : 'default',
  } as const;
  return onPress ? <button type="button" onClick={onPress} style={style}>{content}</button> : <div style={style}>{content}</div>;
}

type Status = 'ready' | 'processing' | 'not-set-up' | 'completed' | 'rejected';

export function StatusPill({ status, label }: { status: Status; label?: string }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const complete = status === 'ready' || status === 'completed';
  const icon = complete ? 'check' : status === 'rejected' ? 'xmark' : 'circle';
  const fallback: Record<Status, string> = {
    ready: t('ui.status.ready'),
    processing: t('ui.status.processing'),
    'not-set-up': t('ui.status.notSetUp'),
    completed: t('ui.status.completed'),
    rejected: t('ui.status.rejected'),
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 32, padding: '5px 10px', borderRadius: 999, backgroundColor: complete ? colors.accentSoft : colors.line, color: colors.text, fontSize: 14, fontWeight: 600 }}>
      <Icon name={icon} size={12} color={complete ? colors.accent : colors.textMuted} />
      {label || fallback[status]}
    </span>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
  helpLink,
}: {
  icon: string;
  title: string;
  body: string;
  action?: { label: string; onPress: () => void };
  helpLink?: { label: string; onPress: () => void };
}) {
  const { colors } = useTheme();
  return (
    <div style={{ padding: '40px 12px', textAlign: 'center' }}>
      <span style={{ width: 56, height: 56, display: 'grid', placeItems: 'center', margin: '0 auto 16px', borderRadius: 28, backgroundColor: colors.line }}>
        <Icon name={icon} size={22} color={colors.text} />
      </span>
      <div style={{ fontSize: 20, fontWeight: 700, color: colors.text }}>{title}</div>
      <div style={{ fontSize: 16, lineHeight: 1.5, color: colors.textSub, margin: '8px auto 20px', maxWidth: 340 }}>{body}</div>
      {action ? <Button label={action.label} onClick={action.onPress} /> : null}
      {helpLink ? <Button label={helpLink.label} variant="text" onClick={helpLink.onPress} style={{ marginTop: 8 }} /> : null}
    </div>
  );
}

export function AmountInput({ value, onChange, suggestions = [] }: { value: string; onChange: (value: string) => void; suggestions?: number[] }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  return (
    <div>
      <div style={{ minHeight: 72, display: 'flex', alignItems: 'center', padding: '0 16px', borderRadius: 12, backgroundColor: colors.line }}>
        <span style={{ fontSize: 28, fontWeight: 700, color: colors.text }}>$</span>
        <input aria-label={t('ui.amount')} inputMode="decimal" value={value} onChange={event => onChange(event.target.value)} style={{ width: '100%', border: 0, outline: 0, background: 'transparent', color: colors.text, fontSize: 28, fontWeight: 700, padding: '0 8px' }} />
      </div>
      {suggestions.length ? (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${suggestions.length}, 1fr)`, gap: 8, marginTop: 10 }}>
          {suggestions.map(amount => <button type="button" key={amount} onClick={() => onChange(String(amount))} style={{ minHeight: 48, borderRadius: 12, border: `1px solid ${colors.line}`, background: colors.surface, color: colors.text, fontSize: 15, fontWeight: 600 }}>${amount}</button>)}
        </div>
      ) : null}
    </div>
  );
}

export function MoneySummary({ rows }: { rows: { label: string; value: ReactNode; emphasis?: boolean }[] }) {
  const { colors } = useTheme();
  return (
    <div style={{ padding: 16, borderRadius: 14, backgroundColor: colors.line }}>
      {rows.map((row, index) => (
        <div key={`${row.label}-${index}`} style={{ minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, borderTop: index && row.emphasis ? `1px solid ${colors.muted}` : undefined, paddingTop: index && row.emphasis ? 10 : 0 }}>
          <span style={{ fontSize: row.emphasis ? 16 : 15, fontWeight: row.emphasis ? 600 : 400, color: colors.text }}>{row.label}</span>
          <span className="sls-tnum" style={{ fontSize: row.emphasis ? 17 : 15, fontWeight: row.emphasis ? 700 : 600, color: colors.text, textAlign: 'right' }}>{row.value}</span>
        </div>
      ))}
    </div>
  );
}

export function ReviewSheet({
  open,
  onClose,
  amount,
  destination,
  source,
  remaining,
  timing,
  onConfirm,
  confirmLabel,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  amount: string;
  destination: string;
  source: string;
  remaining?: string;
  timing?: string;
  onConfirm: () => void;
  confirmLabel: string;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Modal open={open} onClose={onClose} contentStyle={{ alignSelf: 'flex-end' }}>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>{t('ui.review.title')}</div>
      <MoneySummary rows={[
        { label: t('ui.review.amount'), value: amount, emphasis: true },
        { label: t('ui.review.from'), value: source },
        { label: t('ui.review.to'), value: destination },
        ...(remaining ? [{ label: t('ui.review.remaining'), value: remaining }] : []),
        ...(timing ? [{ label: t('ui.review.timing'), value: timing }] : []),
      ]} />
      <Button label={confirmLabel} loading={loading} onClick={onConfirm} style={{ marginTop: 20 }} />
      <Button label={t('ui.review.edit')} variant="secondary" onClick={onClose} style={{ marginTop: 10 }} />
    </Modal>
  );
}

export function StatusTimeline({ steps }: { steps: { label: string; state: 'complete' | 'current' | 'rejected' | 'upcoming'; timestamp?: string }[] }) {
  const { colors } = useTheme();
  return (
    <div>
      {steps.map((step, index) => {
        const done = step.state === 'complete';
        const rejected = step.state === 'rejected';
        return (
          <div key={`${step.label}-${index}`} style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: 10, minHeight: 64 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <span style={{ width: 24, height: 24, display: 'grid', placeItems: 'center', borderRadius: 12, backgroundColor: done ? colors.accentSoft : colors.line }}>
                <Icon name={done ? 'check' : rejected ? 'xmark' : 'circle'} size={11} color={done ? colors.accent : colors.textMuted} />
              </span>
              {index < steps.length - 1 ? <span style={{ width: 2, flex: 1, backgroundColor: colors.line }} /> : null}
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: step.state === 'current' || rejected ? 600 : 400, color: colors.text }}>{step.label}</div>
              {step.timestamp ? <div style={{ marginTop: 3, fontSize: 14, color: colors.textMuted }}>{step.timestamp}</div> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
